import { useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import FileDropZone from '@/components/common/FileDropZone'
import { createBadge, previewBadge } from '@/api/badges'

type Tab = 'url' | 'json' | 'file'

type Props = {
  open: boolean
  onClose: () => void
}

export default function BadgeCreateSheet({ open, onClose }: Props) {
  const qc = useQueryClient()
  const [tab, setTab] = useState<Tab>('url')
  const [url, setUrl] = useState('')
  const [manualName, setManualName] = useState('')
  const [jsonText, setJsonText] = useState('')
  const [jsonError, setJsonError] = useState('')
  const [pendingJson, setPendingJson] = useState<Record<string, unknown> | null>(null)
  const [isFetchingPreview, setIsFetchingPreview] = useState(false)
  const previewRequestRef = useRef(0)

  const addBadge = useMutation({
    mutationFn: (payload: Parameters<typeof createBadge>[0]) => createBadge(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['badges'] })
      handleClose()
      toast.success('バッジを登録しました')
    },
    onError: () => toast.error('登録に失敗しました'),
  })

  const handleClose = () => {
    setTab('url')
    setUrl('')
    setManualName('')
    setJsonText('')
    setJsonError('')
    setPendingJson(null)
    setIsFetchingPreview(false)
    onClose()
  }

  const fetchPreview = async (data: { badge_url?: string; json_data?: Record<string, unknown> }) => {
    const requestId = ++previewRequestRef.current
    setIsFetchingPreview(true)
    try {
      const preview = await previewBadge(data)
      if (requestId !== previewRequestRef.current) return
      if (preview.badge_name) setManualName(preview.badge_name)
    } catch {
      // フェッチ失敗時はサイレントに手動入力に委ねる
    } finally {
      if (requestId === previewRequestRef.current) setIsFetchingPreview(false)
    }
  }

  const handleUrlBlur = () => {
    if (!url || isFetchingPreview) return
    fetchPreview({ badge_url: url })
  }

  const handleUrlSubmit = () => {
    if (!url) return
    addBadge.mutate({ badge_url: url, ...(manualName ? { badge_name: manualName } : {}) })
  }

  const handleJsonPreview = () => {
    setJsonError('')
    let parsed: Record<string, unknown>
    try {
      parsed = JSON.parse(jsonText)
    } catch {
      setJsonError('JSON の形式が正しくありません')
      return
    }
    fetchPreview({ json_data: parsed })
  }

  const handleJsonSubmit = () => {
    setJsonError('')
    let parsed: Record<string, unknown>
    try {
      parsed = JSON.parse(jsonText)
    } catch {
      setJsonError('JSON の形式が正しくありません')
      return
    }
    addBadge.mutate({ json_data: parsed, ...(manualName ? { badge_name: manualName } : {}) })
  }

  const handleFile = (file: File) => {
    const reader = new FileReader()
    reader.onload = (ev) => {
      const text = ev.target?.result as string
      try {
        const parsed = JSON.parse(text)
        const nameFromFile = file.name.replace(/\.json$/i, '')
        setManualName(nameFromFile)
        setPendingJson(parsed)
        fetchPreview({ json_data: parsed })
      } catch {
        toast.error('JSON ファイルの読み込みに失敗しました')
      }
    }
    reader.readAsText(file)
  }

  const handleFileSubmit = () => {
    if (!pendingJson) return
    addBadge.mutate({ json_data: pendingJson, ...(manualName ? { badge_name: manualName } : {}) })
  }

  const tabs: { key: Tab; label: string }[] = [
    { key: 'url', label: 'URL' },
    { key: 'json', label: 'JSON貼り付け' },
    { key: 'file', label: 'ファイル' },
  ]

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) handleClose() }}>
      <SheetContent side="bottom" className="rounded-t-2xl">
        <SheetHeader>
          <SheetTitle>バッジを登録</SheetTitle>
        </SheetHeader>

        {/* タブ */}
        <div className="flex gap-1 mb-4 bg-muted rounded-lg p-1">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => { setTab(t.key); setManualName('') }}
              className={`flex-1 text-xs py-1.5 rounded-md transition-colors ${
                tab === t.key
                  ? 'bg-background shadow-sm font-medium'
                  : 'text-muted-foreground'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* URL タブ */}
        {tab === 'url' && (
          <div className="space-y-3">
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onBlur={handleUrlBlur}
              placeholder="https://..."
              type="url"
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              Open Badges v2 / v3 の assertion URL を入力してフォーカスを外すとバッジ名を自動取得します
            </p>
            <Input
              value={manualName}
              onChange={(e) => setManualName(e.target.value)}
              placeholder={isFetchingPreview ? '取得中...' : 'バッジ名（自動取得、または手動で編集）'}
              disabled={isFetchingPreview}
            />
            <Button
              className="w-full"
              onClick={handleUrlSubmit}
              disabled={!url || addBadge.isPending}
            >
              {addBadge.isPending ? '登録中...' : '追加する'}
            </Button>
          </div>
        )}

        {/* JSON 貼り付けタブ */}
        {tab === 'json' && (
          <div className="space-y-3">
            <Textarea
              value={jsonText}
              onChange={(e) => { setJsonText(e.target.value); setJsonError('') }}
              placeholder={'{\n  "@context": "https://w3id.org/openbadges/v2",\n  ...\n}'}
              rows={5}
              className="font-mono text-xs"
              autoFocus
            />
            {jsonError && <p className="text-xs text-destructive">{jsonError}</p>}
            <div className="flex gap-2">
              <Input
                value={manualName}
                onChange={(e) => setManualName(e.target.value)}
                placeholder={isFetchingPreview ? '取得中...' : 'バッジ名（自動取得できない場合に入力）'}
                disabled={isFetchingPreview}
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleJsonPreview}
                disabled={!jsonText || isFetchingPreview}
                className="shrink-0"
              >
                名前を取得
              </Button>
            </div>
            <Button
              className="w-full"
              onClick={handleJsonSubmit}
              disabled={!jsonText || addBadge.isPending}
            >
              {addBadge.isPending ? '登録中...' : '追加する'}
            </Button>
          </div>
        )}

        {/* ファイルタブ */}
        {tab === 'file' && (
          <div className="space-y-3">
            {pendingJson ? (
              <div className="p-3 bg-muted rounded-lg text-xs text-muted-foreground break-all">
                ✓ ファイル読み込み済み
              </div>
            ) : (
              <FileDropZone
                accept=".json,application/json"
                maxSizeBytes={5 * 1024 * 1024}
                onFile={handleFile}
                label="JSON ファイルを選択またはドロップ"
                sublabel=".json ファイルをタップして選択、またはここにドロップ"
              />
            )}
            <Input
              value={manualName}
              onChange={(e) => setManualName(e.target.value)}
              placeholder={isFetchingPreview ? '取得中...' : 'バッジ名（自動取得、または手動で編集）'}
              disabled={isFetchingPreview}
            />
            <p className="text-xs text-muted-foreground">
              バッジ名が正しくなければ上で編集してください
            </p>
            {pendingJson && (
              <Button
                className="w-full"
                onClick={handleFileSubmit}
                disabled={addBadge.isPending || isFetchingPreview}
              >
                {addBadge.isPending ? '登録中...' : '追加する'}
              </Button>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}
